import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { desksEndpoints as endpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  AgentCapacityOverride,
  AgentCapacityOverrides,
  CapacityOverridePayload,
  CapacityPolicy,
  NewCapacityPolicy,
} from '@/shared/types/capacity-policy';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

/** Fields the UI lets a user edit on the policy (everything except identity/timestamps). */
export type SavePolicyInput = Omit<NewCapacityPolicy, 'desk_id'>;

/** Stable reference so consumers' effects don't re-run on every render while loading. */
const EMPTY_OVERRIDES: AgentCapacityOverride[] = [];

export const API = Object.freeze({
  getPolicy: async (deskId: string): Promise<CapacityPolicy> =>
    callGet(endpoints.capacityPolicy(deskId)),

  createPolicy: async (deskId: string, body: NewCapacityPolicy): Promise<CapacityPolicy> =>
    callPost(endpoints.capacityPolicy(deskId), body),

  updatePolicy: async (deskId: string, body: Partial<CapacityPolicy>): Promise<CapacityPolicy> =>
    callPut(endpoints.capacityPolicy(deskId), body),

  deletePolicy: async (deskId: string): Promise<void> =>
    callDelete(endpoints.capacityPolicy(deskId)),

  listOverrides: async (deskId: string): Promise<AgentCapacityOverrides> =>
    callGet(endpoints.capacityOverrides(deskId)),

  upsertOverride: async (
    deskId: string,
    userId: string,
    body: CapacityOverridePayload
  ): Promise<AgentCapacityOverride> => callPut(endpoints.capacityOverride(deskId, userId), body),

  deleteOverride: async (deskId: string, userId: string): Promise<void> =>
    callDelete(endpoints.capacityOverride(deskId, userId)),
});

/**
 * Reads + writes the capacity policy and per-agent overrides for a desk.
 *
 * Mutations are exposed as plain async functions (not react-query mutations) so the
 * caller can orchestrate the full save sequence — desk PUT, policy POST/PUT, then the
 * overrides diff — under a single Save button and a single success toast, then call
 * `refresh()` to resync the caches.
 */
const useCapacityPolicy = (deskId?: string) => {
  const queryClient = useQueryClient();
  const canRead = useSelector((state: RootState) => getPermissions(state, 'desks', actions.READ));

  const { data: policyData, status: policyStatus } = useQuery<CapacityPolicy, Error>({
    queryKey: [endpoints.capacityPolicy(deskId)],
    queryFn: () => API.getPolicy(deskId),
    enabled: !!deskId && canRead,
  });

  const { data: overridesData, status: overridesStatus } = useQuery<AgentCapacityOverrides, Error>({
    queryKey: [endpoints.capacityOverrides(deskId)],
    queryFn: () => API.listOverrides(deskId),
    enabled: !!deskId && canRead,
  });

  // An errored GET means "no policy / no overrides" — the policy was deleted (the overrides
  // list 404s once its parent policy is gone) or the endpoint isn't deployed (MOV-16784).
  // TanStack Query keeps the last successful `data` alongside the error, so reading it
  // directly would keep serving the pre-delete config.
  const policy = policyStatus === 'error' ? undefined : policyData;
  const overrides =
    overridesStatus === 'error' ? EMPTY_OVERRIDES : (overridesData?.overrides ?? EMPTY_OVERRIDES);

  /** Create when no policy is persisted yet (`policy_id === null`), otherwise update. */
  const savePolicy = useCallback(
    async (input: SavePolicyInput): Promise<CapacityPolicy> => {
      if (!deskId) {
        return Promise.reject(new Error('Missing desk id'));
      }
      const create = () => API.createPolicy(deskId, { ...input, desk_id: deskId });

      let saved: CapacityPolicy;
      if (policy?.policy_id) {
        try {
          saved = await API.updatePolicy(deskId, input);
        } catch (error) {
          // The cache said a policy existed, but the server disagrees (deleted on a prior
          // disable, removed in another tab, or a stale cache after `deletePolicy`). Fall back
          // to create instead of surfacing a spurious 404.
          if ((error as ApiError)?.statusCode !== 404) {
            throw error;
          }
          saved = await create();
        }
      } else {
        saved = await create();
      }
      // Write the result into the cache immediately — a `refresh()`-triggered refetch may not
      // land before the next save, and without this the next save would re-POST instead of PUT.
      queryClient.setQueryData([endpoints.capacityPolicy(deskId)], saved);
      return saved;
    },
    [deskId, policy?.policy_id, queryClient]
  );

  /**
   * Remove the desk's capacity policy (cascades its per-agent overrides server-side). Used when
   * human chat is turned off, so re-enabling starts from defaults rather than the pre-disable
   * config. A 404 is treated as success — nothing to delete because no policy was ever persisted,
   * or the capacity-policy endpoint isn't deployed on this env (MOV-16784).
   */
  const deletePolicy = useCallback(async (): Promise<void> => {
    if (!deskId) {
      return Promise.reject(new Error('Missing desk id'));
    }
    try {
      await API.deletePolicy(deskId);
    } catch (error) {
      if ((error as ApiError)?.statusCode !== 404) {
        throw error;
      }
    }
    // Seed the caches with the known end state instead of invalidating. An invalidated query
    // keeps serving the pre-delete data (status stays 'success') until its refetch lands,
    // letting consumers re-hydrate the config that was just deleted. Removing the policy
    // query flips it to 'pending' so they wait for the fresh desk-derived default instead.
    queryClient.setQueryData([endpoints.capacityOverrides(deskId)], { overrides: [] });
    queryClient.removeQueries({ queryKey: [endpoints.capacityPolicy(deskId)] });
  }, [deskId, queryClient]);

  const upsertOverride = useCallback(
    (userId: string, body: CapacityOverridePayload) => {
      if (!deskId) {
        return Promise.reject(new Error('Missing desk id'));
      }
      return API.upsertOverride(deskId, userId, body);
    },
    [deskId]
  );

  const deleteOverride = useCallback(
    async (userId: string): Promise<void> => {
      if (!deskId) {
        return Promise.reject(new Error('Missing desk id'));
      }
      try {
        await API.deleteOverride(deskId, userId);
      } catch (error) {
        // Already gone — cascade-deleted with its policy, removed in another tab, or the
        // endpoint isn't deployed. The override being absent is the desired end state, so a
        // 404 is success, not an error to surface mid-save.
        if ((error as ApiError)?.statusCode !== 404) {
          throw error;
        }
      }
    },
    [deskId]
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [endpoints.capacityPolicy(deskId)] });
    queryClient.invalidateQueries({ queryKey: [endpoints.capacityOverrides(deskId)] });
  }, [deskId, queryClient]);

  return {
    policy,
    policyStatus,
    overrides,
    overridesStatus,
    savePolicy,
    deletePolicy,
    upsertOverride,
    deleteOverride,
    refresh,
  };
};

export default useCapacityPolicy;
